import React, { useMemo, ChangeEventHandler } from 'react';
import { useIntl } from 'react-intl';
import { Input, Label } from 'reactstrap';
import { site } from '@whitelabel/helpers/site';
import { countriesToSelectOptions } from '@whitelabel/helpers/forms/transform';
import { ILabelValuePair, IManualAddressFieldsOverride } from '@whitelabel/helpers/types';
import { getReactSelectValue } from '@whitelabel/helpers/utils';
import { StyledSelect } from '../../styled/StyledSelect';
import { IManualInputAddress } from './types';
import messages from './messages';
import { getStatesOfCountry } from './helper';
import { StyledManualAddressInput } from './styled';

interface IManualAddressInputProps {
  name: string;
  value: IManualInputAddress;
  setFieldValue: any;
  setFieldTouched: any;
  placeholder?: string;
  setGlobalError?: (...args: any[]) => void;
  disabled?: boolean;
  className?: string;
  fieldsOverride?: IManualAddressFieldsOverride;
}

const ManualAddressInput = ({
  className,
  name,
  value: fieldValue,
  setFieldTouched,
  setFieldValue,
  fieldsOverride,
}: IManualAddressInputProps): JSX.Element => {
  const { formatMessage, locale } = useIntl();
  const countryOptions = useMemo(() => countriesToSelectOptions(site.countries, locale), []);

  const handleTextInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { name: targetName, value: targetValue } = event.target;
    setFieldTouched(name, true);
    setFieldValue(name, { ...fieldValue, [targetName]: targetValue });
  };

  const handleCountryChange = (option: ILabelValuePair | null) => {
    if (option) {
      setFieldValue(name, { ...fieldValue, country: option.value, region: '' });
    }
  };
  const handleStateChange = (option: ILabelValuePair | null) => {
    if (option) {
      setFieldValue(name, { ...fieldValue, region: option.value });
    }
  };

  const regionOptions = getStatesOfCountry(fieldValue.country, locale);
  return (
    <StyledManualAddressInput className={className}>
      <div className="input-wrapper">
        <Label htmlFor="address-line-1">{formatMessage(messages.addressLine1)}</Label>
        <Input
          id="address-line-1"
          type="text"
          name="address1"
          autoComplete="address-line1"
          onChange={handleTextInputChange}
          value={fieldValue.address1}
          {...(fieldsOverride?.address1 ?? {})}
        />
      </div>
      <div className="input-wrapper">
        <Label htmlFor="address-line-2">{formatMessage(messages.addressLine2)}</Label>
        <Input
          id="address-line-2"
          type="text"
          name="address2"
          autoComplete="address-line2"
          onChange={handleTextInputChange}
          value={fieldValue.address2}
          {...(fieldsOverride?.address2 ?? {})}
        />
      </div>
      <div className="input-wrapper half-width">
        <Label htmlFor="country">{formatMessage(messages.country)}</Label>
        <StyledSelect
          id="country"
          name="country"
          classNamePrefix="react-select"
          autoComplete="country"
          options={countryOptions}
          onChange={handleCountryChange}
          value={getReactSelectValue(fieldValue.country, countryOptions)}
          {...(fieldsOverride?.country ?? {})}
        />
      </div>
      <div className="input-wrapper half-width">
        <Label htmlFor="region">{formatMessage(messages.stateRegion)}</Label>
        <StyledSelect
          id="region"
          name="region"
          autoComplete="off"
          classNamePrefix="react-select"
          isSearchable={false}
          options={fieldValue.country ? regionOptions : []}
          isDisabled={!fieldValue.country}
          onChange={handleStateChange}
          value={getReactSelectValue(fieldValue.region, regionOptions)}
          {...(fieldsOverride?.region ?? {})}
        />
      </div>
      <div className="input-wrapper half-width">
        <Label htmlFor="city">{formatMessage(messages.city)}</Label>
        <Input
          id="city"
          name="city"
          type="text"
          autoComplete="address-level2"
          onChange={handleTextInputChange}
          value={fieldValue.city}
          {...(fieldsOverride?.city ?? {})}
        />
      </div>
      <div className="input-wrapper half-width">
        <Label htmlFor="postcode">{formatMessage(messages.postcode)}</Label>
        <Input
          id="postcode"
          name="postcode"
          type="text"
          autoComplete="postal-code"
          onChange={handleTextInputChange}
          value={fieldValue.postcode}
          {...(fieldsOverride?.postcode ?? {})}
        />
      </div>
    </StyledManualAddressInput>
  );
};

export default ManualAddressInput;
